import React, { useState } from 'react'
import type { AdminPuzzleInput, PuzzleNode } from '@chessx/shared/types'

import FenBoard from './FenBoard'
import {
  WALK_MOODS,
  getWalkProgress,
  playCorrectMove,
  playOpponentMove,
  playWrongMove,
  startWalk,
  type WalkState
} from './puzzleWalkthrough'

const BOARD_SIZE_PX = 400

const OTHER_MOVE = -1

const MOOD_LABELS = {
  coach: 'Coach',
  prompt: 'Your move',
  wrong: 'Wrong move',
  correct: 'Correct'
} as const

const isPlayable = (puzzle: AdminPuzzleInput | null): puzzle is AdminPuzzleInput =>
  Boolean(puzzle?.opponentMove?.fenBefore && puzzle.root?.fen && puzzle.root.solution)

const safeStart = (puzzle: AdminPuzzleInput | null): WalkState | null => {
  try {
    return isPlayable(puzzle) ? startWalk(puzzle) : null
  } catch {
    return null
  }
}

interface MistakePick {
  node: PuzzleNode
  index: number
}

const defaultMistakeIndex = (node: PuzzleNode): number => (node.mistakes.length ? 0 : OTHER_MOVE)

interface PuzzlePlayerProps {
  puzzle: AdminPuzzleInput | null
}

const PuzzlePlayer = ({ puzzle }: PuzzlePlayerProps) => {
  const [walk, setWalk] = useState<WalkState | null>(() => safeStart(puzzle))
  const [mistakePick, setMistakePick] = useState<MistakePick | null>(null)

  if (!isPlayable(puzzle) || !walk) {
    return (
      <div className="pz-player pz-player-empty">
        Fix the puzzle JSON to preview it. It needs opponentMove.fenBefore and a root node.
      </div>
    )
  }

  const step = (next: (state: WalkState) => WalkState) => {
    try {
      setWalk(next(walk))
    } catch (error) {
      setWalk({
        ...walk,
        issues: [...walk.issues, error instanceof Error ? error.message : String(error)]
      })
    }
  }

  const mistakes = walk.node.mistakes
  const mistakeIndex =
    mistakePick?.node === walk.node ? mistakePick.index : defaultMistakeIndex(walk.node)
  const progress = getWalkProgress(walk, puzzle)
  const mood = WALK_MOODS[walk.phase]
  const flipped = puzzle.sideToMove === 'black'
  const canPlayMove = walk.phase === 'yourMove' || walk.phase === 'wrong'
  const selectedMistake = mistakeIndex === OTHER_MOVE ? undefined : mistakes[mistakeIndex]
  const stepsDone =
    walk.phase === 'correct' || walk.phase === 'solved' ? progress.index + 1 : progress.index

  return (
    <div className="pz-player">
      <FenBoard
        fen={walk.fen}
        flipped={flipped}
        size={BOARD_SIZE_PX}
        lastMove={walk.lastMove}
        showCoordinates
      />

      <div className="pz-steps" aria-label={`Move ${progress.index + 1} of ${progress.count}`}>
        {Array.from({ length: progress.count }, (_, index) => (
          <span key={index} className={`pz-step ${index < stepsDone ? 'done' : ''}`} />
        ))}
        <span className="pz-steps-label">
          Move {Math.min(progress.index + 1, progress.count)} of {progress.count}
        </span>
      </div>

      <div className="pz-player-actions">
        <button
          type="button"
          className="pz-btn pz-btn-ghost"
          onClick={() =>
            walk.phase === 'intro'
              ? step(() => playOpponentMove(puzzle))
              : setWalk(startWalk(puzzle))
          }
        >
          {walk.phase === 'intro' ? 'Start' : 'Restart'}
        </button>
        <button
          type="button"
          className="pz-btn pz-btn-correct"
          disabled={!canPlayMove && walk.phase !== 'correct'}
          onClick={() => step(state => playCorrectMove(state, puzzle))}
        >
          {walk.phase === 'correct' ? 'Continue' : `Correct move · ${walk.node.solution.san}`}
        </button>
        <button
          type="button"
          className="pz-btn pz-btn-danger"
          disabled={!canPlayMove}
          onClick={() => step(state => playWrongMove(state, puzzle, selectedMistake))}
        >
          Wrong move
        </button>
      </div>

      <label className="pz-field pz-mistake-pick">
        <span>Wrong move to play</span>
        <select
          className="pz-input"
          value={mistakeIndex}
          disabled={!canPlayMove}
          onChange={event => setMistakePick({ node: walk.node, index: Number(event.target.value) })}
        >
          {mistakes.map((mistake, index) => (
            <option key={`${mistake.uci}-${index}`} value={index}>
              {mistake.san} · {mistake.kind}
              {mistake.refutation ? ` → ${mistake.refutation.san}` : ''}
            </option>
          ))}
          <option value={OTHER_MOVE}>Any other move (default feedback)</option>
        </select>
      </label>

      <div className={`pz-guide mood-${mood}`}>
        <span className="pz-guide-label">{MOOD_LABELS[mood]}</span>
        <p className="pz-guide-text">{walk.text}</p>
      </div>

      {walk.issues.length > 0 && (
        <ul className="pz-issues">
          {walk.issues.map((issue, index) => (
            <li key={index}>{issue}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default PuzzlePlayer
